/**
 * Templates API
 *
 * RTK Query API wrapper for the templates feature.
 * Enhances the generated API with proper cache tagging for
 * automatic cache invalidation on mutations.
 */

import { enhancedApi } from '@store/generated/templates';

/**
 * Feature-specific tag types for cache management
 */
const TEMPLATES_TAG_TYPES = ['Template', 'TemplateList', 'TemplateLibrary', 'EncounterTypesList'] as const;

/**
 * Templates API - extends the generated API with custom cache tagging
 */
export const templatesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: TEMPLATES_TAG_TYPES,
  endpoints: {
    // List active templates (main picker)
    getV2ProviderNotetemplates: {
      providesTags: result =>
        result
          ? [
              ...result.templates.map(t => ({
                type: 'Template' as const,
                id: t.template.id,
              })),
              { type: 'TemplateList' as const, id: 'LIST' },
            ]
          : [{ type: 'TemplateList' as const, id: 'LIST' }],
    },

    // Browse public library (discovery)
    getV2ProviderNotetemplatesLibrary: {
      providesTags: result =>
        result
          ? [
              ...result.templates.map(t => ({
                type: 'Template' as const,
                id: t.template.id,
              })),
              { type: 'TemplateLibrary' as const, id: 'LIBRARY' },
            ]
          : [{ type: 'TemplateLibrary' as const, id: 'LIBRARY' }],
    },

    // Get single template
    getV2ProviderNotetemplatesByTemplateId: {
      providesTags: (result, _error, { templateId }) => [{ type: 'Template' as const, id: templateId }],
    },

    // Create template
    postV2ProviderNotetemplates: {
      invalidatesTags: [{ type: 'TemplateList', id: 'LIST' }],
    },

    // Update template
    putV2ProviderNotetemplatesByTemplateId: {
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template' as const, id: templateId },
        { type: 'TemplateList' as const, id: 'LIST' },
      ],
    },

    // Delete template
    deleteV2ProviderNotetemplatesByTemplateId: {
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template' as const, id: templateId },
        { type: 'TemplateList' as const, id: 'LIST' },
        // Appointment types carry template_id references — refetch so stale IDs don't reach submission
        { type: 'EncounterTypesList' as const, id: 'LIST' },
      ],
    },

    // Fork template (creates a USER-scoped copy)
    postV2ProviderNotetemplatesByTemplateIdFork: {
      invalidatesTags: [{ type: 'TemplateList' as const, id: 'LIST' }],
    },

    // Add template to list (opt-in for PUBLIC templates)
    postV2ProviderNotetemplatesByTemplateIdAdd: {
      invalidatesTags: [{ type: 'TemplateList' as const, id: 'LIST' }],
    },

    // Remove template from list (opt-out)
    deleteV2ProviderNotetemplatesByTemplateIdRemove: {
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template' as const, id: templateId },
        { type: 'TemplateList' as const, id: 'LIST' },
        // Appointment types carry template_id references — refetch so stale IDs don't reach submission
        { type: 'EncounterTypesList' as const, id: 'LIST' },
      ],
    },

    // Update section content
    putV2ProviderNotetemplatesByTemplateIdSectionsAndSectionId: {
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template' as const, id: templateId },
      ],
    },

    // Reorder templates (bulk update display_order)
    patchV2ProviderNotetemplatesReorder: {
      invalidatesTags: [{ type: 'TemplateList' as const, id: 'LIST' }],
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        // Optimistic update: reorder templates in cache immediately
        const patchResult = dispatch(
          templatesApi.util.updateQueryData('getV2ProviderNotetemplates', undefined, draft => {
            if (!draft?.templates) return;

            // Create a map of template_id -> new display_order
            const orderMap = new Map(
              args.reorderTemplatesRequest.template_orders.map(item => [
                item.template_id,
                item.display_order ?? null,
              ]),
            );

            // Update display_order for affected templates
            draft.templates.forEach(template => {
              const newOrder = orderMap.get(template.template.id);
              if (newOrder !== undefined) {
                template.display_order = newOrder;
              }
            });

            // Re-sort templates: pinned first (by display_order), then by usage count, then by updated_at
            draft.templates.sort((a, b) => {
              // Pinned templates first
              const aPinned = a.display_order !== null && a.display_order !== undefined;
              const bPinned = b.display_order !== null && b.display_order !== undefined;

              if (aPinned && !bPinned) return -1;
              if (!aPinned && bPinned) return 1;

              // If both pinned, sort by display_order
              if (aPinned && bPinned) {
                const aOrder = a.display_order ?? 999;
                const bOrder = b.display_order ?? 999;
                if (aOrder !== bOrder) return aOrder - bOrder;
              }

              // Then by usage count (descending) - not available in current response, skip
              // Then by updated_at (descending)
              const aUpdated = new Date(a.template.updated_at).getTime();
              const bUpdated = new Date(b.template.updated_at).getTime();
              return bUpdated - aUpdated;
            });
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Queries
  useGetV2ProviderNotetemplatesQuery: useGetTemplatesQuery,
  useLazyGetV2ProviderNotetemplatesQuery: useLazyGetTemplatesQuery,
  useGetV2ProviderNotetemplatesLibraryQuery: useGetTemplateLibraryQuery,
  useLazyGetV2ProviderNotetemplatesLibraryQuery: useLazyGetTemplateLibraryQuery,
  useGetV2ProviderNotetemplatesByTemplateIdQuery: useGetTemplateByIdQuery,
  useLazyGetV2ProviderNotetemplatesByTemplateIdQuery: useLazyGetTemplateByIdQuery,

  // Mutations
  usePostV2ProviderNotetemplatesMutation: useCreateTemplateMutation,
  usePutV2ProviderNotetemplatesByTemplateIdMutation: useUpdateTemplateMutation,
  useDeleteV2ProviderNotetemplatesByTemplateIdMutation: useDeleteTemplateMutation,
  usePostV2ProviderNotetemplatesByTemplateIdForkMutation: useForkTemplateMutation,
  usePostV2ProviderNotetemplatesByTemplateIdAddMutation: useAddTemplateMutation,
  useDeleteV2ProviderNotetemplatesByTemplateIdRemoveMutation: useRemoveTemplateMutation,
  usePutV2ProviderNotetemplatesByTemplateIdSectionsAndSectionIdMutation: useUpdateSectionMutation,
  usePatchV2ProviderNotetemplatesReorderMutation: useReorderTemplatesMutation,
} = templatesApi;

